/**
 * SiteGate.tsx — renders the access prompt for the hosted preview build.
 *
 * Read the header of ./gateConfig.ts before relying on this for anything: it
 * is a speed bump, not authentication.
 *
 * Renders `children` untouched when the gate is disabled (no
 * VITE_SITE_GATE_PASSWORD at build time) or already passed, so local dev and
 * ungated builds carry zero behavioural change.
 *
 * Deliberately not internationalised — this screen is for a small internal
 * tester group and adding keys would mean touching both locale catalogs for a
 * surface that never ships to customers.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isSiteGateEnabled,
  hasPassedSiteGate,
  verifySiteGate,
  recordSiteGatePass,
} from './gateConfig';

/**
 * Paths the gate never stands in front of.
 *
 * The update page is sent to people outside the tester group — that is its
 * entire purpose — so a gate password they have never been given would defeat
 * it. It carries nothing confidential: a product update written to be read, and
 * a list of who has read it.
 *
 * Read from `window.location` rather than the router because this component
 * sits above it.
 */
function isUngatedPath(): boolean {
  return window.location.pathname.startsWith('/updates');
}

export default function SiteGate({ children }: { children: ReactNode }) {
  const [granted, setGranted] = useState(hasPassedSiteGate);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [failed, setFailed] = useState(false);

  if (!isSiteGateEnabled || granted || isUngatedPath()) {
    return <>{children}</>;
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (verifySiteGate(user, password)) {
      recordSiteGatePass();
      setGranted(true);
      return;
    }
    setFailed(true);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{ p: 4, width: '100%', maxWidth: 380, borderRadius: 2 }}
      >
        <Typography variant="h6" component="h1" gutterBottom>
          iTow — Test Environment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          For invited testers. This build runs against seeded test data, not
          production records. Enter the access credentials to continue.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={user}
              onChange={(e) => {
                setUser(e.target.value);
                setFailed(false);
              }}
              autoComplete="username"
              autoFocus
              fullWidth
              size="small"
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFailed(false);
              }}
              autoComplete="current-password"
              fullWidth
              size="small"
            />

            {failed && (
              <Alert severity="error" variant="outlined">
                Those credentials are not recognised.
              </Alert>
            )}

            <Button type="submit" variant="contained" fullWidth size="large">
              Enter
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
